import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import * as analyticsApi from 'features/analytics/studio/api/analyticsApi';
import { HOME_ANALYTIC_TYPES } from 'shared/constants/homeWorkspaceAnalyticTypes';
import {
  readHomeWorkspaceCache,
  writeHomeWorkspaceCache,
} from 'shared/utils/homeWorkspaceCache';
import { readInsightsEventsPreload } from 'shared/utils/preloadService';

/**
 * Custom hook for executing insights queries
 */
export const useInsights = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (queryDSL, teamId = null) => {
    setLoading(true);
    setError(null);

    try {
      // Dev-only: helps verify the editor is actually sending updated retention inputs (e.g. Return Event B)
      if (process.env.NODE_ENV !== 'production' && queryDSL?.queryType === 'retention') {
        const def = queryDSL?.definition || {};
        // eslint-disable-next-line no-console
        console.debug('[Retention execute]', {
          startEvent: def.startEvent,
          returnEvent: def.returnEvent,
          retentionRule: def.retentionRule,
          metric: def.metric,
          countSubject: def.countSubject,
          accumulationGroupKey: def.accumulationGroupKey,
          time: queryDSL?.time,
          teamId,
        });
      }
      const response = await analyticsApi.executeInsights(queryDSL, teamId);
      // Funnels may be double-wrapped: response.data = { data: { steps } }; insights use response.data or response.data.data
      let engineResult;
      if (response?.data?.data?.steps) {
        // Double-wrapped funnel result: response.data.data has steps directly
        // This means response.data = {data: {steps: [...]}}, which is what we want
        engineResult = response.data;
      } else if (response?.data?.data) {
        // Could be double-wrapped insights or other structure
        engineResult = response.data.data;
      } else {
        // Single wrapped or direct
        engineResult = response?.data;
      }
      setResult(engineResult);
      return engineResult;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      if (!analyticsApi.isAbortedOrCancelled(err)) {
        setError(errorMessage);
        setResult(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const preview = useCallback(async (queryDSL) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.previewInsights(queryDSL);
      setResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error previewing insights:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const validate = useCallback(async (queryDSL) => {
    try {
      const response = await analyticsApi.validateQueryDSL(queryDSL);
      return response;
    } catch (err) {
      console.error('Error validating Query DSL:', err);
      return { valid: false, errors: [analyticsApi.getErrorMessage(err)] };
    }
  }, []);

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  /** Call before scheduling a debounced execute so the chart does not briefly show stale rows with new query params. */
  const beginNewQuery = useCallback(() => {
    setResult(null);
    setError(null);
    setLoading(true);
  }, []);

  /** If the debounced execute was cancelled (deps changed), clear loading so the UI does not spin forever. */
  const resetLoading = useCallback(() => {
    setLoading(false);
  }, []);

  return {
    result,
    loading,
    error,
    execute,
    preview,
    validate,
    clearResult,
    beginNewQuery,
    resetLoading,
  };
};

/**
 * Custom hook for managing available events and properties
 */
export const useEventsMetadata = () => {
  const [events, setEvents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertyValues, setPropertyValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadEvents = useCallback(async (teamId = null) => {
    setLoading(true);
    setError(null);

    try {
      // Get teamId from parameter or localStorage
      let finalTeamId = teamId;
      if (!finalTeamId) {
        const selectedTeam = JSON.parse(localStorage.getItem('selectedTeamData') || 'null');
        finalTeamId = selectedTeam?._id || selectedTeam?.id || null;
      }

      const preloaded = readInsightsEventsPreload(finalTeamId);
      if (preloaded) {
        const eventsResponse = preloaded.data;
        const list = eventsResponse?.data?.data ?? eventsResponse?.data ?? [];
        const finalEventsList = Array.isArray(list) ? list : [];
        const eventOptions = finalEventsList.map((e) => ({
          name: e.name,
          count: e.count != null ? parseInt(e.count, 10) : undefined,
          source: 'event',
          eventType: e.eventType || e.event_type,
          displayName: e.displayName || e.display_name,
          description: e.description,
          tags: Array.isArray(e.tags) ? e.tags : [],
        }));
        setEvents(eventOptions);
        return eventOptions;
      }

      // 1) Fetch events first so UI can show them quickly
      const eventsResponse = await analyticsApi.fetchEvents(finalTeamId);
      const list = eventsResponse?.data?.data ?? eventsResponse?.data ?? [];
      const finalEventsList = Array.isArray(list) ? list : [];

      // Events with source 'event' (E tag); backend may include definitions + auto descriptions
      const eventOptions = finalEventsList.map((e) => ({
        name: e.name,
        count: e.count != null ? parseInt(e.count, 10) : undefined,
        source: 'event',
        eventType: e.eventType || e.event_type,
        displayName: e.displayName || e.display_name,
        description: e.description,
        tags: Array.isArray(e.tags) ? e.tags : [],
      }));

      setEvents(eventOptions);

      return eventOptions;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      setEvents([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProperties = useCallback(async (eventName) => {
    if (!eventName) {
      setProperties([]);
      return [];
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.fetchEventProperties(eventName);
      setProperties(response.data || []);
      return response.data;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error loading properties:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPropertyValues = useCallback(async (eventName, property, limit = 50) => {
    if (!eventName || !property) {
      setPropertyValues([]);
      return [];
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.fetchPropertyValues(eventName, property, limit);
      setPropertyValues(response.data || []);
      return response.data;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error loading property values:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Load events on mount
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  return {
    events,
    properties,
    propertyValues,
    loading,
    error,
    loadEvents,
    loadProperties,
    loadPropertyValues
  };
};

/** True when boardId is a real DB id (not "new" or invalid) */
function isRealBoardId(boardId) {
  if (!boardId || boardId === 'new') return false;
  const n = Number(boardId);
  return Number.isInteger(n) && !Number.isNaN(n);
}

/**
 * Build options for executeCard when board time override is active (not default).
 * @param {{ mode: string, preset?: string, start?: string, end?: string, interval?: string, timezone?: string }|null} boardTime
 */
export function getExecuteCardOptions(boardTime) {
  if (!boardTime || boardTime.mode === 'default') return {};
  return { boardTimeOverride: boardTime };
}

/**
 * Custom hook for managing cards
 * @param {string} boardId
 * @param {string|number|null} [teamId] - optional team context when viewing a non-default team (avoids 403 on save)
 * @param {{ mode: string, preset?: string, start?: string, end?: string }|null} [boardTime] - board-level time override; when mode is "default" no override is sent
 */
export const useCards = (boardId, teamId = null, boardTime = null) => {
  const [cards, setCards] = useState([]);
  const [cardResults, setCardResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadCards = useCallback(async () => {
    if (!isRealBoardId(boardId)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.fetchCards(boardId, teamId);
      // API returns { success, data: cards[], count }; extract cards array
      const cardsArray = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : (response?.data?.data ?? []));
      setCards(cardsArray);
      return cardsArray;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error loading cards:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [boardId, teamId]);

  const createCard = useCallback(async (cardData) => {
    if (!isRealBoardId(boardId)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.createCard(boardId, cardData, teamId);
      const newCard = response?.data ?? response;
      if (newCard) setCards(prev => [...prev, newCard]);
      return newCard;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error creating card:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [boardId, teamId]);

  const updateCard = useCallback(async (cardId, updates) => {
    if (!isRealBoardId(boardId)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.updateCard(boardId, cardId, updates, teamId);
      const updatedCard = response.data;
      setCards(prev => prev.map(c => c.id === cardId ? updatedCard : c));
      return updatedCard;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error updating card:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [boardId, teamId]);

  const deleteCard = useCallback(async (cardId) => {
    if (!isRealBoardId(boardId)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await analyticsApi.deleteCard(boardId, cardId, teamId);
      setCards(prev => prev.filter(c => c.id !== cardId));
      setCardResults(prev => {
        const newResults = { ...prev };
        delete newResults[cardId];
        return newResults;
      });
      return true;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error deleting card:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [boardId, teamId]);

  // Stable reference: only change when boardTime content changes (avoids effect storm from "execute when cards load")
  const executeOptions = useMemo(
    () => getExecuteCardOptions(boardTime),
    [boardTime?.mode, boardTime?.preset, boardTime?.start, boardTime?.end]
  );

  const executeCard = useCallback(async (cardId) => {
    if (!isRealBoardId(boardId)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsApi.executeCard(boardId, cardId, teamId, executeOptions);
      const result = response.data;
      setCardResults(prev => ({ ...prev, [cardId]: result }));
      return result;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error executing card:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [boardId, teamId, executeOptions]);

  const executeAllCards = useCallback(async () => {
    if (!isRealBoardId(boardId) || cards.length === 0) return;
    setLoading(true);
    setError(null);
    
    try {
      const results = await Promise.all(
        cards.map(async (card) => {
          try {
            const response = await analyticsApi.executeCard(boardId, card.id, teamId, executeOptions);
            return { cardId: card.id, result: response.data, success: true };
          } catch (err) {
            console.error(`Error executing card ${card.id}:`, err);
            return { cardId: card.id, error: analyticsApi.getErrorMessage(err), success: false };
          }
        })
      );
      
      const newResults = {};
      results.forEach(({ cardId, result, error }) => {
        newResults[cardId] = result || { error };
      });
      
      setCardResults(newResults);
      return newResults;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error executing cards:', err);
      return {};
    } finally {
      setLoading(false);
    }
  }, [boardId, teamId, cards, executeOptions]);

  // Load cards on mount (skip when boardId is "new" or invalid)
  useEffect(() => {
    if (isRealBoardId(boardId)) {
      loadCards();
    }
  }, [boardId, loadCards]);

  // Refetch all cards when board time override changes (skip initial mount to avoid double-fetch with "execute when cards load")
  const boardTimeRef = useRef(undefined);
  useEffect(() => {
    if (boardTimeRef.current === undefined) {
      boardTimeRef.current = boardTime;
      return;
    }
    boardTimeRef.current = boardTime;
    if (isRealBoardId(boardId) && cards.length > 0) {
      executeAllCards();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardTime, boardId, cards, executeAllCards]);

  return {
    cards,
    cardResults,
    loading,
    error,
    loadCards,
    createCard,
    updateCard,
    deleteCard,
    executeCard,
    executeAllCards
  };
};

function getInitialHomeCanvasState(teamId, enabled) {
  if (!enabled || !teamId) {
    return { workspace: null, cards: [], cardResults: {} };
  }
  const cached = readHomeWorkspaceCache(teamId);
  if (!cached) {
    return { workspace: null, cards: [], cardResults: {} };
  }
  return {
    workspace: cached.workspace ?? null,
    cards: Array.isArray(cached.cards) ? cached.cards : [],
    cardResults: cached.cardResults && typeof cached.cardResults === 'object' ? cached.cardResults : {},
  };
}

/**
 * Personal home workspace (per team): same surface as useCards, with initial batch execute from server.
 */
export const useHomeWorkspaceCanvas = (teamId, boardTime, enabled) => {
  const initial = getInitialHomeCanvasState(teamId, enabled);
  const [workspace, setWorkspace] = useState(initial.workspace);
  const [cards, setCards] = useState(initial.cards);
  const [cardResults, setCardResults] = useState(initial.cardResults);
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [error, setError] = useState(null);

  const executeOptions = useMemo(
    () => getExecuteCardOptions(boardTime),
    [boardTime?.mode, boardTime?.preset, boardTime?.start, boardTime?.end]
  );

  const loadFull = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    const includeResults = opts.includeResults !== undefined ? opts.includeResults : true;
    if (!enabled || !teamId) return;
    if (!silent) setSetupLoading(true);
    setError(null);
    try {
      const response = await analyticsApi.fetchHomeWorkspace(teamId, { includeResults });
      const payload = response?.data;
      const ws = payload?.workspace ?? null;
      const list = payload?.cards ?? [];
      const arr = Array.isArray(list) ? list : [];
      const raw = payload?.results;
      setWorkspace(ws);
      setCards(arr);
      if (includeResults) {
        if (raw && typeof raw === 'object') {
          const mapped = {};
          Object.keys(raw).forEach((k) => {
            mapped[k] = raw[k];
          });
          setCardResults(mapped);
        } else {
          setCardResults({});
        }
      } else if (!includeResults) {
        setCardResults((prev) => {
          const next = { ...prev };
          const validIds = new Set(arr.map((c) => String(c.id)));
          Object.keys(next).forEach((k) => {
            if (!validIds.has(String(k))) delete next[k];
          });
          return next;
        });
      }
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      if (!silent) setError(errorMessage);
      console.error('Error loading home workspace:', err);
    } finally {
      if (!silent) setSetupLoading(false);
    }
  }, [enabled, teamId]);

  useLayoutEffect(() => {
    if (!enabled || !teamId) {
      setWorkspace(null);
      setCards([]);
      setCardResults({});
      setSetupLoading(false);
      setError(null);
      return;
    }
    const cached = readHomeWorkspaceCache(teamId);
    if (cached) {
      setWorkspace(cached.workspace ?? null);
      setCards(Array.isArray(cached.cards) ? cached.cards : []);
      setCardResults(
        cached.cardResults && typeof cached.cardResults === 'object' ? cached.cardResults : {}
      );
      setSetupLoading(false);
      setError(null);
      void loadFull({ silent: true, includeResults: false });
      return;
    }
    void loadFull({ silent: false, includeResults: true });
  }, [enabled, teamId, loadFull]);

  /** Execute only analytic cards that have no cached result (fast return from editor vs re-running every chart). */
  useEffect(() => {
    if (!enabled || !teamId || cards.length === 0) return;
    const missing = cards.filter((c) => {
      const t = (c.cardType || '').toLowerCase();
      if (!HOME_ANALYTIC_TYPES.has(t)) return false;
      const id = c.id;
      const r = cardResults[id] ?? cardResults[String(id)];
      return r == null;
    });
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const pairs = await Promise.all(
          missing.map(async (c) => {
            try {
              const response = await analyticsApi.executeHomeWorkspaceCard(c.id, teamId, executeOptions);
              const result = response?.data;
              return { id: c.id, result };
            } catch (e) {
              const msg = analyticsApi.getErrorMessage(e);
              return { id: c.id, result: { error: msg, cardId: c.id } };
            }
          })
        );
        if (cancelled) return;
        setCardResults((prev) => {
          const next = { ...prev };
          pairs.forEach(({ id, result }) => {
            if (result != null) {
              next[id] = result;
              next[String(id)] = result;
            }
          });
          return next;
        });
      } catch (err) {
        const errorMessage = analyticsApi.getErrorMessage(err);
        setError(errorMessage);
        console.error('Error executing home workspace cards (missing results):', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, teamId, cards, cardResults, executeOptions]);

  const loadCards = useCallback(async () => {
    if (!enabled || !teamId) return [];
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsApi.fetchHomeWorkspace(teamId, { includeResults: false });
      const payload = response?.data;
      setWorkspace(payload?.workspace ?? null);
      const list = payload?.cards ?? [];
      const arr = Array.isArray(list) ? list : [];
      setCards(arr);
      return arr;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error loading home workspace cards:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled, teamId]);

  useEffect(() => {
    if (!enabled || !teamId || setupLoading) return;
    writeHomeWorkspaceCache(teamId, { workspace, cards, cardResults });
  }, [enabled, teamId, setupLoading, workspace, cards, cardResults]);

  const executeAllCards = useCallback(async () => {
    if (!enabled || !teamId || cards.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsApi.executeHomeWorkspace(teamId, executeOptions);
      const raw = response?.data?.results ?? {};
      const newResults = {};
      Object.keys(raw).forEach((k) => {
        newResults[k] = raw[k];
      });
      setCardResults(newResults);
      return newResults;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error executing home workspace:', err);
      return {};
    } finally {
      setLoading(false);
    }
  }, [enabled, teamId, cards.length, executeOptions]);

  const boardTimeRef = useRef(undefined);
  useEffect(() => {
    if (!enabled) return;
    if (boardTimeRef.current === undefined) {
      boardTimeRef.current = boardTime;
      return;
    }
    boardTimeRef.current = boardTime;
    if (teamId && cards.length > 0) {
      executeAllCards();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardTime, enabled, teamId, cards, executeAllCards]);

  const createCard = useCallback(async (cardData) => {
    if (!enabled || !teamId) return null;
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsApi.createHomeWorkspaceCard(cardData, teamId);
      const newCard = response?.data;
      if (newCard) setCards((prev) => [...prev, newCard]);
      return newCard;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error creating home workspace card:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [enabled, teamId]);

  const updateCard = useCallback(async (cardId, updates) => {
    if (!enabled || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsApi.updateHomeWorkspaceCard(cardId, updates, teamId);
      const updatedCard = response?.data;
      setCards((prev) => prev.map((c) => (c.id === cardId ? updatedCard : c)));
      return updatedCard;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error updating home workspace card:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [enabled, teamId]);

  const deleteCard = useCallback(async (cardId) => {
    if (!enabled || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      await analyticsApi.deleteHomeWorkspaceCard(cardId, teamId);
      setCards((prev) => prev.filter((c) => c.id !== cardId));
      setCardResults((prev) => {
        const next = { ...prev };
        delete next[cardId];
        delete next[String(cardId)];
        return next;
      });
      return true;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error deleting home workspace card:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [enabled, teamId]);

  const executeCard = useCallback(async (cardId) => {
    if (!enabled || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsApi.executeHomeWorkspaceCard(cardId, teamId, executeOptions);
      const result = response?.data;
      setCardResults((prev) => ({ ...prev, [cardId]: result }));
      return result;
    } catch (err) {
      const errorMessage = analyticsApi.getErrorMessage(err);
      setError(errorMessage);
      console.error('Error executing home workspace card:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [enabled, teamId, executeOptions]);

  return {
    workspace,
    cards,
    cardResults,
    loading,
    setupLoading,
    error,
    loadCards,
    loadFull,
    createCard,
    updateCard,
    deleteCard,
    executeCard,
    executeAllCards
  };
};

export default useInsights;
